/**
 * React-Admin Auth Provider
 * Auth0 integration for admin panel
 */

import { AuthProvider, UserIdentity } from 'react-admin';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { ENV } from '../config/environment';
import { logger } from '../utils/logger';

// ============================================================
// AUTH0 CLIENT SINGLETON
// ============================================================

let auth0Client: Auth0Client | null = null;
let initPromise: Promise<Auth0Client> | null = null;
let callbackHandled = false;
let adminAccessDenied = false;

/**
 * CRITICAL: Capture Auth0 callback params at module load time
 * This runs BEFORE any React components render, ensuring we don't lose
 * the callback params if something normalizes the URL.
 */
let capturedCallbackParams: { code: string; state: string } | null = null;

if (typeof window !== 'undefined') {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  if (code && state) {
    capturedCallbackParams = { code, state };
    logger.info('Auth0 callback params captured at module load');
  }
}

/**
 * Initialize Auth0 client (singleton)
 */
async function getAuth0Client(): Promise<Auth0Client> {
  if (auth0Client) {
    return auth0Client;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    logger.info('Initializing Auth0 client...');

    const client = new Auth0Client({
      domain: ENV.AUTH0_DOMAIN,
      clientId: ENV.AUTH0_CLIENT_ID,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: ENV.AUTH0_AUDIENCE,
        scope: ENV.AUTH0_SCOPE,
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
    });

    // Handle redirect callback if we have params (only once)
    const hasCallbackParams = capturedCallbackParams !== null;
    const currentQuery = window.location.search;
    const hasCurrentParams = currentQuery.includes('code=') && currentQuery.includes('state=');

    if ((hasCallbackParams || hasCurrentParams) && !callbackHandled) {
      callbackHandled = true;
      logger.info('Processing Auth0 callback...');

      try {
        if (hasCallbackParams && !hasCurrentParams) {
          // URL was stripped, use captured params
          const callbackUrl = `${window.location.origin}${window.location.pathname}?code=${capturedCallbackParams!.code}&state=${capturedCallbackParams!.state}`;
          await client.handleRedirectCallback(callbackUrl);
        } else {
          await client.handleRedirectCallback();
        }

        logger.info('Auth0 callback processed successfully');
        capturedCallbackParams = null;

        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        logger.error('Auth0 callback failed:', error);
        capturedCallbackParams = null;

        // Clean URL to prevent loop
        window.history.replaceState({}, document.title, window.location.pathname);

        // Clear stale Auth0 state
        cleanupAuth0Cache();
      }
    }

    auth0Client = client;
    return client;
  })();

  return initPromise;
}

/**
 * Clean up Auth0 cache on failure
 */
function cleanupAuth0Cache() {
  try {
    const auth0Keys = Object.keys(localStorage).filter(
      (key) => key.includes('auth0') || key.includes('@@auth0spajs@@')
    );
    auth0Keys.forEach((key) => localStorage.removeItem(key));
    logger.info('Auth0 cache cleared');
  } catch (err) {
    logger.error('Error clearing Auth0 cache:', err);
  }
}

/**
 * Get access token from Auth0
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const client = await getAuth0Client();
    const isAuth = await client.isAuthenticated();
    if (!isAuth) {
      return null;
    }

    const token = await client.getTokenSilently({
      authorizationParams: {
        audience: ENV.AUTH0_AUDIENCE,
        scope: ENV.AUTH0_SCOPE,
      },
    });
    return token;
  } catch (error) {
    logger.error('Error getting access token:', error);
    return null;
  }
}

/**
 * Force refresh access token
 */
export async function forceRefreshToken(): Promise<string | null> {
  try {
    const client = await getAuth0Client();
    const token = await client.getTokenSilently({
      authorizationParams: {
        audience: ENV.AUTH0_AUDIENCE,
        scope: ENV.AUTH0_SCOPE,
      },
      cacheMode: 'off',
    });
    return token;
  } catch (error) {
    logger.error('Error refreshing token:', error);
    return null;
  }
}

// ============================================================
// REACT-ADMIN AUTH PROVIDER
// ============================================================

export const authProvider: AuthProvider = {
  /**
   * Login - redirect to Auth0
   * IMPORTANT: Returns a promise that never resolves because loginWithRedirect()
   * resolves before the browser actually navigates. If we let it resolve,
   * react-admin will continue processing, checkAuth will fail, and logout
   * will be called, causing a loop.
   */
  login: async (params?: { forceLogin?: boolean }) => {
    const client = await getAuth0Client();
    adminAccessDenied = false;
    logger.info('Redirecting to Auth0 login...');

    // Start the redirect
    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: ENV.AUTH0_AUDIENCE,
        scope: ENV.AUTH0_SCOPE,
        ...(params?.forceLogin ? { prompt: 'login' } : {}),
      },
    });

    // Return a promise that never resolves - browser will redirect away
    // This prevents react-admin from continuing to process after redirect
    return new Promise(() => {});
  },

  /**
   * Logout - clear Auth0 session
   * IMPORTANT: Must check if authenticated first because react-admin
   * calls logout when checkAuth fails (prevents infinite loop)
   */
  logout: async () => {
    // React Admin invokes logout after checkAuth rejects. A role refusal is not
    // an expired session: preserve app SSO and show a stable access-denied page.
    if (adminAccessDenied) return '/login?reason=access-denied';
    logger.info('Logging out...');
    const client = await getAuth0Client();

    // Check if user is actually authenticated before calling Auth0 logout
    // React-admin calls logout when checkAuth fails, causing loops
    const isAuth = await client.isAuthenticated();

    if (!isAuth) {
      logger.info('User not authenticated, skipping Auth0 logout');
      // Just clean up local state, don't redirect to Auth0
      cleanupAuth0Cache();
      return;
    }

    // Reset state
    auth0Client = null;
    initPromise = null;
    callbackHandled = false;

    // Clean up local cache
    cleanupAuth0Cache();

    // Logout from Auth0 (redirects to Auth0 logout endpoint)
    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  },

  /**
   * Check if user is authenticated AND is an admin
   */
  checkAuth: async () => {
    const client = await getAuth0Client();
    const isAuth = await client.isAuthenticated();

    if (!isAuth) {
      throw new Error('Not authenticated');
    }

    // Verify admin status via backend
    try {
      const token = await client.getTokenSilently({
        authorizationParams: {
          audience: ENV.AUTH0_AUDIENCE,
          scope: ENV.AUTH0_SCOPE,
        },
      });

      const response = await fetch(`${ENV.API_URL}/admin/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          adminAccessDenied = true;
          throw Object.assign(new Error('Administrator access is required.'), { redirectTo: '/login?reason=access-denied' });
        }
        logger.error('Admin verification failed:', response.status);
        throw new Error('Admin verification failed');
      }

      const data = await response.json();

      if (!data.isAdmin) {
        adminAccessDenied = true;
        throw Object.assign(new Error('Administrator access is required.'), { redirectTo: '/login?reason=access-denied' });
      }

      adminAccessDenied = false;
      logger.info('Admin status verified');
    } catch (error) {
      logger.error('Admin check failed:', error);
      throw error;
    }
  },

  /**
   * Check API errors
   * In react-admin, throwing here triggers logout
   * We don't want to auto-logout on 401/403 - let checkAuth handle it
   */
  checkError: async () => {
    // Don't throw - react-admin's checkAuth will handle auth state
    // Throwing here causes logout loops
    return;
  },

  /**
   * Get user identity for display
   */
  getIdentity: async (): Promise<UserIdentity> => {
    const client = await getAuth0Client();
    const user = await client.getUser();

    if (!user) {
      throw new Error('No user found');
    }

    return {
      id: user.sub || '',
      fullName: user.name || user.email || 'Admin User',
      avatar: user.picture,
    };
  },

  /**
   * Get permissions
   */
  getPermissions: async () => {
    return ['admin'];
  },
};
